import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Action {
  text: string;
  timestamp: string;
  type: 'progress' | 'bot' | 'milestone';
}

interface GoalData {
  currentGoal: string;
  goalVision?: string;
  goalWhy?: string;
  goalSetDate: string;
  actions: Action[];
  streak: number;
  totalActions: number;
  milestones: string[];
  lastCheckin: string;
}

interface Idea {
  id: string;
  text: string;
  timestamp: string;
}

import { buildApiUrl } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

export const GoalYet: React.FC = () => {
  const [goalData, setGoalData] = useState<GoalData | null>(null);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [goalStep, setGoalStep] = useState<'goal' | 'vision' | 'ready'>('goal');
  const [goalId, setGoalId] = useState<number | null>(null);
  const [showIdeaModal, setShowIdeaModal] = useState(false);
  const [showIdeasList, setShowIdeasList] = useState(false);
  const [showDecomposeModal, setShowDecomposeModal] = useState(false);
  const [ideaInput, setIdeaInput] = useState('');
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [decomposeTask, setDecomposeTask] = useState('');
  const [decomposedSteps, setDecomposedSteps] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { token } = useAuth();

  // Load data from API or localStorage on mount
  useEffect(() => {
    const loadGoal = async () => {
      if (token) {
        try {
          const response = await fetch(buildApiUrl('/goals/active'), {
            headers: { Authorization: `Bearer ${token}` }
          });
          const data = await response.json();

          if (data.goal) {
            setGoalId(data.goal.id);
            setGoalData({
              currentGoal: data.goal.goal,
              goalVision: data.goal.vision,
              goalWhy: data.goal.why,
              goalSetDate: data.goal.goal_set_date,
              actions: data.actions.map((a: any) => ({
                text: a.action_text,
                timestamp: a.timestamp,
                type: a.action_type
              })),
              streak: data.goal.streak_days || 0,
              totalActions: data.goal.total_actions || 0,
              milestones: [],
              lastCheckin: data.goal.last_checkin || new Date().toISOString()
            });
            setGoalStep('ready');
          } else {
            // Try localStorage fallback
            const stored = localStorage.getItem('goalyet-data');
            if (stored) {
              setGoalData(JSON.parse(stored));
            } else {
              // First time setup
              addBotMessage("Welcome to GoalYet™! ⚠️ WARNING: Your goal will be stored FOREVER. Even if abandoned, it remains in your history. Choose wisely. What's your ONE goal? (Suggestion: Launch GoalYet™ to 100 users)");
            }
          }
        } catch (error) {
          console.error('Failed to load goal:', error);
          // Fallback to localStorage
          const stored = localStorage.getItem('goalyet-data');
          if (stored) {
            setGoalData(JSON.parse(stored));
          } else {
            addBotMessage("Welcome to GoalYet™! ⚠️ WARNING: Your goal will be stored FOREVER. Even if abandoned, it remains in your history. Choose wisely. What's your ONE goal? (Suggestion: Launch GoalYet™ to 100 users)");
          }
        }
      } else {
        // No token, use localStorage
        const stored = localStorage.getItem('goalyet-data');
        if (stored) {
          setGoalData(JSON.parse(stored));
        } else {
          addBotMessage("Welcome to GoalYet™! What's your ONE goal? Be specific. (Suggestion: Launch GoalYet™ to 100 users)");
        }
      }
    };

    loadGoal();
  }, [token]);

  // Save to localStorage whenever goalData changes
  useEffect(() => {
    if (goalData) {
      localStorage.setItem('goalyet-data', JSON.stringify(goalData));
      checkStreak();
    }
  }, [goalData]);

  // Auto-focus input
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Load ideas from localStorage
  useEffect(() => {
    const storedIdeas = localStorage.getItem('goalyet-ideas');
    if (storedIdeas) {
      setIdeas(JSON.parse(storedIdeas));
    }
  }, []);

  // Save ideas to localStorage
  useEffect(() => {
    if (ideas.length > 0) {
      localStorage.setItem('goalyet-ideas', JSON.stringify(ideas));
    }
  }, [ideas]);

  const addIdea = () => {
    if (!ideaInput.trim()) return;

    const newIdea: Idea = {
      id: Date.now().toString(),
      text: ideaInput.trim(),
      timestamp: new Date().toISOString()
    };

    setIdeas(prev => [newIdea, ...prev]);
    setIdeaInput('');
    setShowIdeaModal(false);

    // Add as an action too
    addUserAction(`💡 Idea: ${ideaInput.trim()}`);
  };

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [goalData?.actions]);

  const checkStreak = () => {
    if (!goalData) return;
    
    const lastDate = new Date(goalData.lastCheckin);
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - lastDate.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays > 1) {
      // Streak broken
      setGoalData(prev => prev ? { ...prev, streak: 0 } : null);
    }
  };

  const addBotMessage = (text: string) => {
    const action: Action = {
      text,
      timestamp: new Date().toISOString(),
      type: 'bot'
    };
    
    if (!goalData) {
      // Initial setup mode
      setGoalData({
        currentGoal: '',
        goalSetDate: new Date().toISOString(),
        actions: [action],
        streak: 0,
        totalActions: 0,
        milestones: [],
        lastCheckin: new Date().toISOString()
      });
    } else {
      setGoalData(prev => prev ? {
        ...prev,
        actions: [...prev.actions, action]
      } : null);
    }
  };

  const addUserAction = (text: string) => {
    const action: Action = {
      text,
      timestamp: new Date().toISOString(),
      type: 'progress'
    };
    
    setGoalData(prev => {
      if (!prev) return null;
      
      const today = new Date().toDateString();
      const lastCheckinDate = new Date(prev.lastCheckin).toDateString();
      const isNewDay = today !== lastCheckinDate;
      
      return {
        ...prev,
        actions: [...prev.actions, action],
        totalActions: prev.totalActions + 1,
        streak: isNewDay ? prev.streak + 1 : prev.streak,
        lastCheckin: new Date().toISOString()
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    
    const userInput = input.trim().toLowerCase();
    setInput('');
    
    // Handle first-time goal setting flow
    if (!goalData?.currentGoal) {
      const goal = input.trim();
      setGoalData(prev => prev ? {
        ...prev,
        currentGoal: goal,
        actions: [...prev.actions, {
          text: `Goal set: ${goal}`,
          timestamp: new Date().toISOString(),
          type: 'milestone'
        }]
      } : null);

      // Save to API if logged in
      if (token) {
        try {
          const response = await fetch(buildApiUrl('/goals'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({ goal })
          });
          const data = await response.json();
          if (data.goalId) {
            setGoalId(data.goalId);
          }
        } catch (error) {
          console.error('Failed to save goal:', error);
        }
      }

      setGoalStep('vision');
      setTimeout(() => {
        addBotMessage("🔥 Powerful goal! Now tell me: WHY do you want this? What's your vision when you achieve it? (This helps me coach you better)");
      }, 500);
      return;
    }

    // Handle vision/why step
    if (goalStep === 'vision' && !goalData?.goalVision) {
      const vision = input.trim();
      setGoalData(prev => prev ? {
        ...prev,
        goalVision: vision,
        goalWhy: vision,
        actions: [...prev.actions, {
          text: `Vision: ${vision}`,
          timestamp: new Date().toISOString(),
          type: 'bot'
        }]
      } : null);

      // Update goal with vision in API
      if (token && goalId) {
        try {
          // For now, we'll store it as an action
          await fetch(buildApiUrl(`/goals/${goalId}/actions`), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              action_text: `Vision: ${vision}`,
              action_type: 'milestone'
            })
          });
        } catch (error) {
          console.error('Failed to save vision:', error);
        }
      }

      setGoalStep('ready');
      setTimeout(() => {
        addBotMessage("🎯 VISION LOCKED IN! I understand your 'why' now. Let's make it happen! What's the smallest step you can take RIGHT NOW? (Opening your IDE counts!)");
      }, 500);
      return;
    }
    
    // Add user message to history
    addUserAction(input.trim());

    // Save action to API if logged in
    if (token && goalId) {
      try {
        fetch(buildApiUrl(`/goals/${goalId}/actions`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            action_text: input.trim(),
            action_type: 'progress'
          })
        });
      } catch (error) {
        console.error('Failed to save action:', error);
      }
    }

    // Process commands and responses
    setIsTyping(true);

    // Get AI coaching if available
    if (token && goalId && !userInput.includes('done') && !userInput.includes('stats') && !userInput.includes('goal')) {
      try {
        const response = await fetch(buildApiUrl(`/goals/${goalId}/coach`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            message: input.trim(),
            context: userInput === 'no' ? 'stuck' : 'progress'
          })
        });

        if (response.ok) {
          const data = await response.json();
          setTimeout(() => {
            addBotMessage(data.message);
            setIsTyping(false);
          }, 500);
          return;
        }
      } catch (error) {
        console.error('AI coaching failed:', error);
      }
    }

    // Fallback to local responses
    setTimeout(() => {
      handleUserInput(userInput, input.trim());
      setIsTyping(false);
    }, 800);
  };

  const handleUserInput = (input: string, originalInput: string) => {
    // Special commands
    if (input === 'goal') {
      addBotMessage(`Your goal: ${goalData?.currentGoal}`);
      return;
    }
    
    if (input === 'stats') {
      addBotMessage(`📊 Stats: ${goalData?.totalActions} actions • ${goalData?.streak} day streak • ${goalData?.milestones.length} milestones`);
      return;
    }
    
    if (input === 'change goal') {
      addBotMessage("⚠️ Warning: Changing goals breaks momentum. Type your new goal or 'cancel' to keep current.");
      return;
    }
    
    if (input === 'done') {
      if (goalData && goalData.totalActions > 0) {
        addBotMessage(`🎆 Session complete! ${goalData.totalActions} actions toward your goal. Come back tomorrow to maintain your ${goalData.streak} day streak!`);
      }
      return;
    }
    
    if (input === 'stuck') {
      const microSteps = [
        "Open your code editor. That's it. Just open it.",
        "Write one line. Any line. Even a comment.",
        "Create one file. Empty is fine.",
        "Google one thing about your blocker.",
        "Write down what's blocking you in 5 words."
      ];
      const step = microSteps[Math.floor(Math.random() * microSteps.length)];
      addBotMessage(`Let's decompose: ${step}`);
      return;
    }
    
    // Progress responses
    if (input === 'no' || input.includes('haven\'t') || input.includes('nothing')) {
      const pushResponses = [
        `${goalData?.totalActions} actions taken. You're closer than yesterday. Next micro-step? Opening a code editor counts.`,
        "Your goal awaits. 5 minutes. One tiny action. What?",
        "Perfectionism is procrastination. Ship garbage. What garbage will you ship?",
        "Your competition moved forward today. Your move. Smallest action?",
        "Zero to one. Not zero to perfect. What's your 'one'?"
      ];
      const response = pushResponses[Math.floor(Math.random() * pushResponses.length)];
      addBotMessage(response);
    } else {
      // User reported progress!
      const celebrateResponses = [
        `YES! '${originalInput}' done! Momentum building. What's next?`,
        `🔥 Progress is progress! '${originalInput}' moves you closer. Continue?`,
        `⚡ '${originalInput}' completed! You're ${goalData?.totalActions} actions in. Another?`,
        `SHIPPED! '${originalInput}' is in the books. Keep rolling?`,
        `🚀 '${originalInput}' = forward motion! What's the next micro-step?`
      ];
      const response = celebrateResponses[Math.floor(Math.random() * celebrateResponses.length)];
      addBotMessage(response);
      
      // Check for milestones
      if (goalData && goalData.totalActions % 10 === 0) {
        setTimeout(() => {
          addBotMessage(`🎆 MILESTONE! ${goalData.totalActions} actions completed!`);
        }, 1500);
      }
    }
  };

  const getMomentumLevel = () => {
    if (!goalData) return 0;
    const recent = goalData.actions.filter(a => {
      const actionTime = new Date(a.timestamp).getTime();
      const now = new Date().getTime();
      const hourAgo = now - (60 * 60 * 1000);
      return actionTime > hourAgo && a.type === 'progress';
    }).length;
    
    if (recent >= 5) return 3; // On fire
    if (recent >= 3) return 2; // Building
    if (recent >= 1) return 1; // Started
    return 0; // Cold
  };

  const momentum = getMomentumLevel();
  const momentumColors = ['bg-gray-400', 'bg-yellow-400', 'bg-orange-400', 'bg-red-500'];
  const momentumLabels = ['Cold Start', 'Building', 'Momentum', '🔥 ON FIRE'];

  if (!goalData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-teal-50">
        <div className="text-2xl text-gray-400">Loading GoalYet™...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-12rem)]">
      {/* Compact Goal Status */}
      {goalData.currentGoal && (
        <div className="bg-white border border-orange-100 rounded-lg p-4 mb-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="text-sm text-gray-500 mb-1">Current Goal</div>
              <div className="text-lg font-semibold text-gray-900">{goalData.currentGoal}</div>
            </div>
            <div className="flex items-center space-x-6">
              <div className="text-center">
                <div className="text-xs text-gray-500">Streak</div>
                <div className="text-lg font-bold text-orange-600">🔥 {goalData.streak}</div>
              </div>
              <div className="text-center">
                <div className="text-xs text-gray-500">Actions</div>
                <div className="text-lg font-bold text-teal-600">⚡ {goalData.totalActions}</div>
              </div>
              <button
                onClick={() => setShowIdeaModal(true)}
                className="text-xl bg-orange-100 hover:bg-orange-200 px-2 py-1 rounded-full transition-all hover:scale-110 text-orange-600"
                title="Capture an idea"
              >
                💡
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-6 space-y-3">
        <AnimatePresence>
          {goalData.actions.map((action, idx) => (
            <motion.div
              key={`${action.timestamp}-${idx}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`${
                action.type === 'bot' 
                  ? 'flex justify-start'
                  : action.type === 'milestone'
                  ? 'flex justify-center'
                  : 'flex justify-end'
              }`}
            >
              <div className={`max-w-lg px-4 py-3 rounded-lg ${
                action.type === 'bot'
                  ? 'bg-gray-100 text-gray-800'
                  : action.type === 'milestone'
                  ? 'bg-gradient-to-r from-orange-500 to-teal-500 text-white font-semibold'
                  : 'bg-gradient-to-r from-orange-500 to-orange-600 text-white'
              }`}>
                {action.type === 'milestone' && <span className="mr-2">🎯</span>}
                {action.text}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        
        {isTyping && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex justify-start"
          >
            <div className="bg-gray-100 px-4 py-3 rounded-lg">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </motion.div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Commands */}
      <div className="bg-white border-t border-gray-200 px-6 py-3">
        <div className="flex flex-wrap gap-2 text-xs">
          <button
            onClick={() => setShowDecomposeModal(true)}
            className="px-4 py-2 bg-gradient-to-r from-orange-500 to-teal-500 text-white rounded-full hover:from-orange-600 hover:to-teal-600 transition-all font-bold flex items-center gap-1"
          >
            🔨 DECOMPOSE
          </button>
          {['stuck', 'stats', 'goal', 'done'].map(cmd => (
            <button
              key={cmd}
              onClick={() => setInput(cmd)}
              className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full hover:bg-orange-200 transition-colors"
            >
              {cmd}
            </button>
          ))}
        </div>
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="bg-white border-t border-gray-200 p-4">
        <div className="flex space-x-3">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={
              !goalData?.currentGoal ? "Enter your ONE goal..." :
              goalStep === 'vision' ? "Why do you want this? What's your vision?" :
              "Goal yet? (Report any progress or type 'no')"
            }
            className="flex-1 px-4 py-3 border-2 border-orange-200 rounded-lg focus:outline-none focus:border-orange-500 text-lg"
          />
          <button
            type="submit"
            className="px-8 py-3 bg-gradient-to-r from-orange-500 to-teal-500 text-white rounded-lg hover:from-orange-600 hover:to-teal-600 transition-all font-semibold"
          >
            →
          </button>
        </div>
        <div className="mt-2 text-xs text-gray-500 text-center">
          Progress {'>'} Perfection • Momentum {'>'} Magnitude • Action {'>'} Planning
        </div>
      </form>

      {/* Idea Modal */}
      {showIdeaModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 max-w-md w-full"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold flex items-center">
                <span className="text-2xl mr-2">💡</span>
                Capture Your Idea
              </h3>
              <button
                onClick={() => setShowIdeaModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <textarea
              value={ideaInput}
              onChange={(e) => setIdeaInput(e.target.value)}
              placeholder="What's your idea? Be specific..."
              className="w-full px-4 py-3 border-2 border-orange-200 rounded-lg focus:outline-none focus:border-orange-500 text-lg resize-none"
              rows={4}
              autoFocus
            />

            <div className="flex items-center justify-between mt-4">
              <button
                onClick={() => {
                  setShowIdeasList(true);
                  setShowIdeaModal(false);
                }}
                className="text-orange-600 hover:text-orange-700 text-sm"
              >
                View all ideas ({ideas.length})
              </button>

              <div className="space-x-3">
                <button
                  onClick={() => setShowIdeaModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={addIdea}
                  disabled={!ideaInput.trim()}
                  className="px-6 py-2 bg-gradient-to-r from-orange-500 to-teal-500 text-white rounded-lg hover:from-orange-600 hover:to-teal-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save Idea
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Decompose Modal */}
      {showDecomposeModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-bold flex items-center">
                <span className="text-3xl mr-2">🔨</span>
                DECOMPOSE
              </h3>
              <button
                onClick={() => {
                  setShowDecomposeModal(false);
                  setDecomposeTask('');
                  setDecomposedSteps([]);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="text-sm text-gray-600 mb-4">
              Break down ANY task into micro-steps. The smaller the better!
            </div>

            <textarea
              value={decomposeTask}
              onChange={(e) => setDecomposeTask(e.target.value)}
              placeholder="What task feels overwhelming? (e.g., 'Build landing page', 'Write documentation', 'Fix authentication bug')"
              className="w-full px-4 py-3 border-2 border-orange-200 rounded-lg focus:outline-none focus:border-orange-500 text-lg resize-none"
              rows={3}
              autoFocus
            />

            <button
              onClick={async () => {
                if (!decomposeTask.trim()) return;

                const task = decomposeTask.trim();

                // Try API decomposition first
                if (token) {
                  try {
                    const response = await fetch(buildApiUrl('/decompose'), {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                      },
                      body: JSON.stringify({
                        task,
                        context: goalData?.currentGoal || 'General task'
                      })
                    });

                    if (response.ok) {
                      const data = await response.json();
                      if (data.steps && data.steps.length > 0) {
                        setDecomposedSteps(data.steps.map((step: string, idx: number) =>
                          `${['🎯', '📂', '👀', '✍️', '💾', '🔄', '✅', '🚀', '📝', '⚡', '🎨', '🔧'][idx] || '•'} ${step}`
                        ));
                        return;
                      }
                    }
                  } catch (error) {
                    console.error('API decomposition failed:', error);
                  }
                }

                // Fallback to local decomposition
                const steps = decomposeTask.toLowerCase().includes('landing')
                  ? [
                      '🎯 Open your code editor',
                      '📁 Create a new file: landing.html',
                      '✍️ Type: <html>',
                      '📝 Add: <head><title>',
                      '💭 Write your page title',
                      '🏷️ Close: </title></head>',
                      '📦 Add: <body>',
                      '✨ Write: <h1>Your Headline</h1>',
                      '🎨 Add one paragraph of text',
                      '🔚 Close: </body></html>',
                      '💾 Save the file',
                      '🌐 Open in browser',
                      '✅ Landing page exists!'
                    ]
                  : decomposeTask.toLowerCase().includes('auth')
                  ? [
                      '🔍 Open the auth file',
                      '👀 Read the error message',
                      '📋 Copy the error text',
                      '🔎 Search for the error line',
                      '💡 Add console.log above it',
                      '🔄 Refresh and check console',
                      '🤔 Identify what\'s undefined',
                      '✏️ Add a null check',
                      '🧪 Test the login flow',
                      '✅ Bug fixed!'
                    ]
                  : decomposeTask.toLowerCase().includes('doc')
                  ? [
                      '📄 Create README.md',
                      '✍️ Type: # Project Name',
                      '⏎ Press Enter twice',
                      '📝 Write one sentence description',
                      '⏎ Press Enter twice',
                      '✍️ Type: ## Installation',
                      '📋 Add: npm install',
                      '💾 Save file',
                      '✅ Docs started!'
                    ]
                  : [
                      `📂 Open project folder`,
                      `👀 Look at "${task}"`,
                      `🤔 Identify first tiny piece`,
                      `🎯 Focus on ONLY that piece`,
                      `⌨️ Type one line of code`,
                      `💾 Save your progress`,
                      `✅ First micro-step done!`,
                      `🔄 Repeat for next piece`
                    ];

                setDecomposedSteps(steps);
              }}
              disabled={!decomposeTask.trim()}
              className="mt-4 w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-teal-500 text-white rounded-lg hover:from-orange-600 hover:to-teal-600 disabled:opacity-50 disabled:cursor-not-allowed font-bold text-lg"
            >
              🔨 DECOMPOSE THIS TASK
            </button>

            {decomposedSteps.length > 0 && (
              <div className="mt-6 flex-1 overflow-y-auto">
                <h4 className="font-bold mb-3 text-orange-700">Micro-Steps (Do ONE at a time):</h4>
                <div className="space-y-2">
                  {decomposedSteps.map((step, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg border border-orange-200 hover:bg-orange-100 cursor-pointer transition-colors"
                      onClick={() => {
                        setInput(step.replace(/^[^\s]+ /, ''));
                        setShowDecomposeModal(false);
                        setDecomposeTask('');
                        setDecomposedSteps([]);
                      }}
                    >
                      <span className="font-bold text-orange-600">{idx + 1}.</span>
                      <span className="flex-1">{step}</span>
                    </motion.div>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    <strong>Pro tip:</strong> Click any step to add it to your current action. Each step should take less than 5 minutes!
                  </p>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Ideas List Modal */}
      {showIdeasList && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">
                💡 Your Ideas ({ideas.length})
              </h3>
              <button
                onClick={() => setShowIdeasList(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-3">
              {ideas.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No ideas captured yet. Start brainstorming!
                </p>
              ) : (
                ideas.map((idea) => (
                  <div
                    key={idea.id}
                    className="bg-orange-50 border border-orange-200 rounded-lg p-4"
                  >
                    <p className="text-gray-800">{idea.text}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      {new Date(idea.timestamp).toLocaleString()}
                    </p>
                  </div>
                ))
              )}
            </div>

            <div className="mt-4 flex justify-between">
              <button
                onClick={() => {
                  if (confirm('Clear all ideas?')) {
                    setIdeas([]);
                    localStorage.removeItem('goalyet-ideas');
                  }
                }}
                className="text-red-600 hover:text-red-700 text-sm"
              >
                Clear All
              </button>
              <button
                onClick={() => setShowIdeasList(false)}
                className="px-6 py-2 bg-gradient-to-r from-orange-500 to-teal-500 text-white rounded-lg hover:from-orange-600 hover:to-teal-600"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};